import { useEffect, useState } from "react";
import { Modal } from "@/common/components/modal";
import Form from "@/common/components/form/Form";
import { INVENTORY_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { buildEditBatchForm } from "./data";

interface IEditBatchModalProps {
  isOpen: boolean;
  close: () => void;
  batch: KeyValueObj | null;
  onUpdated: (updated: KeyValueObj) => void;
}

// Relocated from the old flat inventory list's row-menu "Edit" action (which
// no longer makes sense once stage-1 rows aggregate multiple batches) — this
// is the same PUT /inventories/:id capability, now living where a single
// batch is actually in view. Only the fields InventoryConcern#update_params
// permits are editable; batch identity/dates/cost are immutable.
export function EditBatchModal({ isOpen, close, batch, onUpdated }: IEditBatchModalProps) {
  const [packageTypeOptions, setPackageTypeOptions] = useState<
    { id: string; title: string }[]
  >([]);
  // MED-698 — whether this batch's owner sells at a level derived from the
  // packaging hierarchy rather than one they choose (a manufacturer). Defaults to
  // false so a server that has not been deployed yet, or a failed fetch, falls
  // back to the pre-MED-698 behaviour rather than silently hiding the field from
  // a reseller who needs it. Read from the server's answer, never from the
  // logged-in company type: the rule is enforced server-side, so a UI that
  // decided this for itself could offer a field the server will refuse. Same
  // switch, same reasoning as SellingLevelPriceModal.
  const [sellingLevelDerived, setSellingLevelDerived] = useState(false);
  // The form waits on this fetch rather than rendering against an unanswered
  // flag. Without it a manufacturer sees "Selling Package Type" appear and then
  // vanish as the response lands, and every seller sees the dropdown briefly
  // empty — it has no options until the layers arrive either.
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const apiCall = useApiCallHandling();

  useEffect(() => {
    if (!isOpen || !batch?.id) return;

    setOptionsLoading(true);
    // Reset rather than carry the previous batch's answer into this one. Invisible
    // behind the loading gate, and it means a failed fetch falls back to the safe
    // default (field shown) instead of to whatever the last batch resolved to.
    setSellingLevelDerived(false);
    apiCall(
      `${INVENTORY_URL}/${batch.id}/selling-package-types`,
      "GET",
      {},
      (data: KeyValueObj) => {
        // MED-698 reshaped this response from a bare array of layers to
        // { layers, selling_level_derived }, because the flag has to travel with
        // the layers to be of any use. `data.layers ?? data` keeps an older
        // server's array shape working rather than rendering an empty dropdown
        // against it.
        const layers: KeyValueObj[] = Array.isArray(data)
          ? data
          : ((data?.layers as KeyValueObj[]) ?? []);
        setSellingLevelDerived(data?.selling_level_derived === true);
        setPackageTypeOptions(
          layers.map((layer: KeyValueObj) => ({
            id: layer.packaging_type,
            title: layer.packaging_type,
          })),
        );
      },
      () => {},
      // Cleared on failure too, so the reorder and defective fields stay editable
      // when the layer lookup is what broke. `sellingLevelDerived` then holds its
      // safe default of false, which shows the field rather than hiding it.
      () => setOptionsLoading(false),
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, batch?.id]);

  function onSubmit(values: KeyValueObj) {
    if (!batch?.id) return;
    setSubmitLoading(true);
    apiCall(
      `${INVENTORY_URL}/${batch.id}`,
      "PUT",
      values,
      (data: KeyValueObj) => {
        onUpdated(data);
        close();
      },
      () => {},
      () => setSubmitLoading(false),
    );
  }

  const formInputs = buildEditBatchForm(packageTypeOptions, sellingLevelDerived).map(
    (field) => ({
      ...field,
      initialValue: batch?.[field.name],
    }),
  );

  return (
    <Modal isOpen={isOpen} close={close} title="Edit Batch">
      <div className="w-[30rem] max-w-[90vw] px-4 py-2">
        {batch && optionsLoading && (
          <p className="py-6 text-center text-sm text-gray-500">Loading batch…</p>
        )}
        {batch && !optionsLoading && (
          <Form
            loadingState={submitLoading}
            submitLabel="Save Changes"
            formInputs={formInputs}
            processInputs={onSubmit}
            spinnerComponent="spinner"
            login={false}
            biControl={false}
          />
        )}
      </div>
    </Modal>
  );
}
